import styled from '@emotion/styled';
import React, { useMemo } from 'react';
import { getDirection } from 'utils';
import Select, { SelectItemProps } from 'ds/components/input/Select';
import { useScreenBreakpoint } from 'consts/breakpoints';
import { BorderRadiusSize } from 'ds';

const AdditionalMenuWrapper = styled.div`
  &&& {
    svg {
      margin: 0;
    }
  }
`;

const ToggleWrapper = styled.div<{ isOpen?: boolean }>(({ isOpen, theme }) => ({
  width: '100%',
  ...(isOpen ? {
    'button': {
      border: `2px solid ${theme.colors.neutrals.grey1} !important`,
    },
  } : {}),
}));

const ItemWrapper = styled.div<{ fillSvg?: boolean }>`
  ${({ theme, fillSvg }) => `
    display: flex;
    align-items: center;
    svg {
      path {
        fill: ${fillSvg ? theme.colors.neutrals.grey1 : 'none'};
      }
    }
  `}
`;

export enum MenuActions {
  Freeze = 'freeze',
  Promote = 'promote',
  Share = 'share',
  CancelSubscription = 'cancelSubscription',
  Delete = 'delete',
  Unfreeze = 'unfreeze',
  Edit = 'edit',
}

export type IAdditionalMenuOption = SelectItemProps & { onChange: () => void; promotedUntil?: Date; };

interface AdditionalMenuProps {
  options: IAdditionalMenuOption[];
  selectStyles?: React.CSSProperties;
  disabled?: boolean;
  borderRadiusSize?: BorderRadiusSize;
  renderItem?: (item: SelectItemProps, isSelected: boolean) => React.ReactNode;
}

const ICONS_WITHOUT_FILL: Set<MenuActions> = new Set([ MenuActions.Promote, MenuActions.Share, MenuActions.CancelSubscription ]);

export const AdditionalMenu: React.FC<AdditionalMenuProps> = React.memo((props) => {
  const isMobile = useScreenBreakpoint() < 3;

  const { children, options, selectStyles, disabled, borderRadiusSize } = props;

  const renderItemFn = props.renderItem || Select.defaultProps.renderItem;

  const shouldResetSelectedValue = () => true;
  const renderItem = (item: SelectItemProps, isSelected: boolean) =>
    <ItemWrapper fillSvg={!ICONS_WITHOUT_FILL.has(item.value as MenuActions)}>{renderItemFn(item, isSelected)}</ItemWrapper>;

  const items = useMemo(() =>
    options.map(({ value, icon, label, promotedUntil }) => ({ value, icon, label, promotedUntil })), [ options ]);

  const onChange = (value: MenuActions) => {
    const option: IAdditionalMenuOption = options.find(v => v.value === value);
    return option.onChange ? option.onChange.call(null) : null;
  };

  return (
    <AdditionalMenuWrapper>
      <Select
        styles={selectStyles}
        isUncontrolled
        borderRadiusSize={borderRadiusSize}
        shouldResetSelectedValue={shouldResetSelectedValue}
        items={items}
        onChange={onChange}
        alignment={getDirection(true, isMobile)}
        renderItem={renderItem}
        renderCustomToggle={( toggleProps, isOpen ) => disabled ? children : (
          <ToggleWrapper {...toggleProps} isOpen={isOpen}>{children}</ToggleWrapper>
        )}
      />
    </AdditionalMenuWrapper>
  );
});
